/**
 * /control/nom151 · Verificador de sellos NOM-151 (Fase 18.2)
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ShieldAlert, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { fmtFechaHora } from "./_common";

export default function Nom151Verificador() {
    const [sellos, setSellos] = useState([]);
    const [filtroTipo, setFiltroTipo] = useState("");
    const [filtroId, setFiltroId] = useState("");
    const [resultadoVerif, setResultadoVerif] = useState(null);

    const load = async () => {
        const params = {};
        if (filtroTipo) params.documento_tipo = filtroTipo;
        if (filtroId) params.documento_id = filtroId;
        const r = await api.get("/control/nom151", { params });
        setSellos(r.data || []);
    };
    useEffect(() => { load(); }, []);

    const verificar = async (selloId) => {
        try {
            // Endpoint público: usar REACT_APP_BACKEND_URL directo
            const base = process.env.REACT_APP_BACKEND_URL;
            const r = await fetch(`${base}/api/public/nom151/verificar/${selloId}`);
            const d = await r.json();
            setResultadoVerif(d);
        } catch (e) {
            setResultadoVerif({ error: String(e) });
        }
    };

    return (
        <div className="space-y-6" data-testid="nom151-list">
            <div>
                <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    NOM-151 · Sellos de Tiempo
                </h1>
                <p className="text-sm text-muted-foreground">
                    Timestamp Authority (TSA) para cumplimiento NOM-151-SCFI-2016 · Sellado automático al generar
                    dictámenes, convenios, actas y estados de cuenta.
                    <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-300 border">MOCK TSA</Badge>
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4" /> Filtros
                    </CardTitle>
                </CardHeader>
                <CardContent className="flex gap-2 flex-wrap">
                    <Input placeholder="Tipo documento (ej. estado_cuenta)" value={filtroTipo}
                        onChange={e => setFiltroTipo(e.target.value)} className="max-w-xs"
                        data-testid="nom151-filtro-tipo" />
                    <Input placeholder="ID documento" value={filtroId}
                        onChange={e => setFiltroId(e.target.value)} className="max-w-xs"
                        data-testid="nom151-filtro-id" />
                    <Button variant="outline" size="sm" onClick={load} data-testid="nom151-aplicar">
                        <RefreshCw className="h-4 w-4 mr-1" /> Aplicar
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Tipo doc.</th>
                                <th className="px-3 py-2 text-left">Folio ref.</th>
                                <th className="px-3 py-2 text-left">Autoridad TSA</th>
                                <th className="px-3 py-2 text-left">N° sello</th>
                                <th className="px-3 py-2 text-left">Fecha sello</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-right">Verificar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sellos.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                                    Sin sellos NOM-151 registrados.
                                </td></tr>
                            )}
                            {sellos.map(s => (
                                <tr key={s.id} className="border-t">
                                    <td className="px-3 py-2 text-xs">{s.documento_referencia?.tipo}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{s.documento_referencia?.folio}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <Badge className="bg-slate-100 text-slate-800 border-slate-300 border">
                                            {s.sello_tsa?.autoridad_tsa}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px]">
                                        {s.sello_tsa?.numero_sello?.slice(0, 24)}…
                                    </td>
                                    <td className="px-3 py-2 text-xs">{fmtFechaHora(s.sello_tsa?.fecha_sello)}</td>
                                    <td className="px-3 py-2">
                                        <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">
                                            {s.estado}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" variant="outline"
                                            onClick={() => verificar(s.id)}
                                            data-testid={`nom151-verif-${s.id}`}>
                                            Verificar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            {resultadoVerif && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            {resultadoVerif.verificacion?.valido
                                ? <><CheckCircle2 className="h-4 w-4 text-emerald-700" /> Verificación válida</>
                                : <><XCircle className="h-4 w-4 text-red-700" /> Verificación falló</>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1" data-testid="nom151-resultado">
                        <p><b>Sello ID:</b> <code>{resultadoVerif.sello_id}</code></p>
                        <p><b>Hash documento:</b> <code className="text-xs">{(resultadoVerif.hash_documento || "").slice(0, 48)}…</code></p>
                        <p><b>Autoridad TSA:</b> {resultadoVerif.autoridad_tsa}</p>
                        <p><b>Fecha sello:</b> {fmtFechaHora(resultadoVerif.fecha_sello)}</p>
                        <p><b>Detalle:</b> {resultadoVerif.verificacion?.detalle}</p>
                        <p className="text-xs text-muted-foreground">
                            Verificación realizada contra TSA (MOCK). Para integrar ADVANTIS/CECOBAN/SAT real, reemplazar
                            `core/tsa_client.py`.
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
